import { z } from "zod";
import { tryParseISODate } from "./date-time-format.ts";
import { Decimal, isDecimal, type TDecimal } from "./decimal.ts";
import { toNullableTrimmedString } from "./string.ts";
import { fromString, type TypeId } from "./typeid.ts";

export const nonEmptyArrayParser = <T>(parser: z.ZodSchema<T>) => z.tuple([parser], parser);

export const zStringReq = (msg?: string) =>
  z
    .string()
    .trim()
    .min(1, { message: msg ?? "Required" });

export const nullableTrimmedString = z
  .string()
  .nullable()
  .transform((val) => toNullableTrimmedString(val));

export const tidSchemaFactory = <T extends string>(prefix: T) =>
  z.custom<TypeId<T>>(
    (val) => {
      try {
        return fromString(val as string, prefix);
      } catch {
        return false;
      }
    },
    { error: (issue) => `Expected a valid TypeId<${prefix}>, got "${issue.input as string}"` },
  );

export const decimalSchema = <T extends TDecimal = TDecimal>({
  nullDefault = false,
  errorMessage,
}: {
  nullDefault?: boolean;
  errorMessage?: string;
} = {}) =>
  z
    .preprocess((val) => {
      if (isDecimal(val)) {
        return val;
      }

      try {
        return Decimal(val as string | number);
      } catch {
        return nullDefault ? null : val;
      }
    }, z.any())
    .pipe(
      z.custom<T>(
        (val) => {
          if (nullDefault && val === null) {
            return true;
          }
          return isDecimal(val) ? val : false;
        },
        {
          error: (issue) => errorMessage ?? `Expected a valid Decimal, got "${issue.input as string}"`,
        },
      ),
    );

export const isoDateOnlySchema = z.string().transform((value, ctx) => {
  const parsedDate = tryParseISODate(value);

  if (parsedDate === null) {
    ctx.addIssue({ code: "custom", message: `Expected a valid ISO date, got "${value}"` });
    return z.NEVER;
  }

  return parsedDate;
});

export const stringOrNumberSchema = () =>
  z.union([z.string(), z.number()]).transform((val) => {
    if (typeof val === "number") {
      return val.toString();
    }
    return val;
  });
